import {
	Box,
	Center,
	Divider,
	Fade,
	Flex,
	HStack,
	Popover,
	PopoverArrow,
	PopoverBody,
	PopoverContent,
	PopoverTrigger,
	SlideFade,
	Spacer,
	Spinner,
	Stack,
} from '@chakra-ui/react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { Button } from '../../../design-system/button';
import { useMenuSize } from '../../../design-system/hooks';
import { ArrowLeftOutline, XOutline } from '../../../design-system/icon';
import { Heading } from '../../../design-system/typography';
import { rem } from '../../../design-system/utils';
import { useLocalStorage } from '../../../lib/storage';
import { useCurrentCompany } from '../../../lib/who-am-i';
import { DimensionKey } from '../../../queries/recognition-analysis';
import { Bizy } from '../../bizy';
import { Reveal } from '../../display/reveal';
import { ErrorBoundary } from '../../error/error-boundary';
import { ReactComponent as AiSparkle } from './ai-sparkle.svg';
import { CupFiller } from './cup-filler';
import { DraftAssistProp } from './hooks/use-draft-assist';
import {
	CompanyValues,
	Dimension,
	DimensionBox,
	DimensionContext,
	DimensionExamples,
	ScoreboxFeedback,
	useDimensionAccordion,
	useScoreboxState,
} from './scorebox-components';
import { SlidingText } from './sliding-text';
import { calculatePercentageStop } from './utils';

type ScoreBoxPopoutProps = {
	dimensions: Dimension[];
	isLoading: boolean;
	text: string;
	draftAssist?: DraftAssistProp;
	hasUnmetDimension?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
};

export const ScoreBoxPopout: FC<ScoreBoxPopoutProps> = ({ dimensions, isLoading, text, draftAssist, hasUnmetDimension, onOpenChange }) => {
	const { t } = useTranslation('recognition', { keyPrefix: 'gratitudeGauge' });
	const isDraftAssistEnabled = useFeatureFlag('bizy-draft-assist');
	// When draft assist is off: restore open state across sessions (original UX).
	// When draft assist is on: always start closed so the LLM only fires on explicit
	// user intent. Session state resets on each modal open — no localStorage restore.
	const [storedIsOpen, setStoredIsOpen] = useLocalStorage('gratitude-gauge-open:desktop', true);
	const [sessionIsOpen, setSessionIsOpen] = useState(false);
	const isOpen = isDraftAssistEnabled ? sessionIsOpen : storedIsOpen;
	const setIsOpen = isDraftAssistEnabled ? setSessionIsOpen : setStoredIsOpen;
	const matches = dimensions.filter((d) => d.matches).length;
	const percentage = calculatePercentageStop(matches, dimensions.length);
	const ref = useMenuSize();
	const state = useScoreboxState();
	const hasCoreValues = useCurrentCompany().core_values.length > 0;
	const { track } = useAnalytics();
	const derivedIndex = draftAssist?.dimensionKey != null ? dimensions.findIndex((d) => d.key === draftAssist.dimensionKey) : null;
	const overrideIndex = derivedIndex !== -1 ? derivedIndex : null;
	const { register } = useDimensionAccordion(overrideIndex);

	const handleToggle = () => {
		const nextIsOpen = !isOpen;

		if (nextIsOpen) {
			track(AnalyticsEvent.GratitudeGauge.Opened, { type: 'popout' });
		} else {
			track(AnalyticsEvent.GratitudeGauge.Closed, { type: 'popout' });
		}

		setIsOpen(nextIsOpen);
		onOpenChange?.(nextIsOpen);
	};

	const bizyState = useMemo(() => (isLoading ? 'thinking' : 'curious'), [isLoading]);

	return (
		<ErrorBoundary>
			<DimensionContext.Provider value={{ stage: state.stage }}>
				<Flex
					direction="row"
					position="absolute"
					left={isDraftAssistEnabled ? 'calc(100% + 24px)' : 'calc(100% - 1rem)'}
					columnGap={isDraftAssistEnabled ? undefined : 2}
					pointerEvents="none"
				>
					{isDraftAssistEnabled ? (
						<Box position="absolute" top={0} right="calc(95%)">
							<SlideFade in={!isOpen} offsetY="10px" unmountOnExit style={{ flexShrink: 0 }}>
								<Popover isOpen={hasUnmetDimension && !isOpen} placement="top" closeOnBlur={false} isLazy>
									<PopoverTrigger>
										<Box aria-label={t('buttonLabel')} as="button" pointerEvents="auto" onClick={handleToggle}>
											<Bizy state={bizyState} size="md" />
										</Box>
									</PopoverTrigger>
									<PopoverContent width="auto" maxW="160px" pointerEvents="none">
										<PopoverArrow />
										<PopoverBody fontSize="xs" textAlign="center" px={3} py={2}>
											{t('draftAssistHint')}
										</PopoverBody>
									</PopoverContent>
								</Popover>
							</SlideFade>
						</Box>
					) : (
						<Center
							aria-label={t('buttonLabel')}
							as="button"
							bgColor={isOpen ? 'purple.400' : 'purple.300'}
							border="2px solid"
							borderColor="bg.surface"
							borderRadius="full"
							boxSize={rem(40)}
							flexShrink={0}
							pointerEvents="auto"
							onClick={handleToggle}
							p={1}
							transitionDuration="normal"
							transitionProperty="background-color"
							_hover={{
								bgColor: 'purple.500',
							}}
						>
							<AiSparkle />
						</Center>
					)}

					<SlideFade in={isOpen} style={{ pointerEvents: isOpen ? 'auto' : 'none' }}>
						<Box w={rem(322)} bg="bg.surface" borderRadius="lg" overflow="hidden" columnGap={4} overflowY="auto" ref={ref}>
							<Flex
								transform={state.isViewing ? `translateX(calc(-100% - ${rem(16)}))` : 'translateX(0)'}
								transition="transform 0.3s cubic-bezier(0.68, -0.3, 0.265, 1.25)"
								columnGap={4}
								w="full"
							>
								<Box w="full" flexShrink={0}>
									<Reveal if={!state.isViewing || state.isTransitioning}>
										{isDraftAssistEnabled ? (
											<HStack columnGap={2} p={2} pr={4} alignItems="center">
												<Bizy state={isLoading ? 'thinking' : 'curious'} size="md" />
												<Heading color="text.default">{t('bizyTitle')}</Heading>
												<Spacer />
												<Fade in={isLoading}>
													<Spinner size="sm" color="purple.300" />
												</Fade>
												<Box as="button" onClick={handleToggle} color="text.subdued" px={1}>
													<XOutline size="xs" />
												</Box>
											</HStack>
										) : (
											<HStack columnGap={2} p={4}>
												<Heading color="text.default">{t('heading')}</Heading>
												<Spacer />

												<Fade in={isLoading}>
													<Spinner size="sm" color="purple.300" />
												</Fade>
											</HStack>
										)}

										{!isDraftAssistEnabled && <Divider />}

										<Stack rowGap={2} p={4}>
											{!isDraftAssistEnabled && (
												<HStack mb={2} columnGap={2}>
													<CupFiller flexShrink={0} percentage={percentage} />
													<SlidingText text={t('fillText', { context: percentage })} />
												</HStack>
											)}

											{dimensions.map((dimension, index) => {
												const isMatchingDimension = draftAssist?.dimensionKey === dimension.key;
												return (
													<DimensionBox
														dimension={dimension}
														key={dimension.key}
														onClick={() => state.onViewDimension(dimension)}
														text={text}
														{...register(index)}
														suggestion={isMatchingDimension ? draftAssist.suggestion : undefined}
														onAddSuggestion={isMatchingDimension && draftAssist?.onAdd ? (t: string) => draftAssist.onAdd?.(t) : undefined}
														onDismissSuggestion={isMatchingDimension ? draftAssist?.onDismiss : undefined}
													/>
												);
											})}
										</Stack>
									</Reveal>
								</Box>

								<Box w="full" flexShrink={0}>
									<Reveal if={state.isViewing || state.isTransitioning}>
										<HStack columnGap={1} p={4}>
											<Box as="button" px={2} onClick={state.onCloseDimension} color="text.default">
												<ArrowLeftOutline size="xs" />
											</Box>
											<Heading color="text.default">{t('exampleTitle', { context: state.dimension?.key })}</Heading>
										</HStack>

										<Divider />

										<Stack rowGap={2} p={4}>
											{state.dimension && (state.dimension.key !== DimensionKey.ValuesAligned || !hasCoreValues) && (
												<DimensionExamples dimension={state.dimension} text={text} />
											)}
											{state.dimension && state.dimension.key === DimensionKey.ValuesAligned && hasCoreValues && <CompanyValues />}

											<Button colorScheme="secondary" variant="alpha" mt={4} onClick={state.onCloseDimension}>
												{t('back')}
											</Button>
										</Stack>
									</Reveal>
								</Box>
							</Flex>

							<ScoreboxFeedback dimensions={dimensions} px={4} pb={4} text={text} />
						</Box>
					</SlideFade>
				</Flex>
			</DimensionContext.Provider>
		</ErrorBoundary>
	);
};
